import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { comics } from '@/mocks/comics';
import { categories } from '@/mocks/categories';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { Helmet } from '@/pages/home/components/SEO';

const categoryNames: Record<string, string> = {};
categories.forEach((c) => { categoryNames[c.slug] = c.name; });

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const comic = comics.find((c) => c.slug === slug);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-20 text-center">
            <h1 className="font-heading text-2xl text-foreground-900 mb-4">未找到该漫画</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const catName = categoryNames[comic.category] || comic.category;
  const relatedComics = comics.filter((c) => c.category === comic.category && c.id !== comic.id).slice(0, 4);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩漫在线观看`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: comic.createdAt,
    dateModified: comic.updatedAt,
    publisher: {
      '@type': 'Organization',
      name: '韩漫网',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://hanmanwang.com/article/${comic.slug}`,
    },
  };

  return (
    <>
      <Helmet
        title={`${comic.title} - 韩漫在线观看 | 韩漫网`}
        description={`${comic.title}，作者${comic.author}。${comic.description}韩漫网提供下拉式韩国漫画在线观看，无需跳转，阅读体验连贯流畅。`}
        keywords={`${comic.title},${comic.author},韩漫在线观看,下拉式韩国漫画,韩漫网,韩漫大全,${catName}漫画`}
        canonicalUrl={`https://hanmanwang.com/article/${comic.slug}`}
        schema={[articleSchema]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="py-4">
              <Breadcrumb items={[
                { label: '首页', href: '/' },
                { label: catName, href: `/category/${comic.category}` },
                { label: comic.title },
              ]} />
            </div>

            <article className="pb-16">
              <div className="flex flex-col lg:flex-row gap-8 md:gap-10 mb-10">
                <div className="w-full lg:w-[280px] shrink-0">
                  <div className="w-[200px] sm:w-[240px] lg:w-full aspect-[3/4] rounded-lg overflow-hidden">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      title={`${comic.title} - 韩漫在线观看`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                    {comic.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="text-sm text-foreground-600">作者：<strong>{comic.author}</strong></span>
                    <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      comic.status === '连载中'
                        ? 'bg-secondary-100 text-secondary-700'
                        : 'bg-background-200 text-foreground-500'
                    }`}>
                      {comic.status}
                    </span>
                    <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                    <span className="flex items-center gap-0.5 text-sm text-accent-600">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-xs"></i>
                      </span>
                      <strong>{comic.rating}</strong>
                    </span>
                    <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                    <span className="text-sm text-foreground-500">{(comic.views / 10000).toFixed(0)}万阅读</span>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-5">
                    {comic.tags.map((tag) => (
                      <span key={tag} className="px-3 py-1 text-xs rounded-full bg-accent-100 text-accent-700 whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                    {comic.description}
                  </p>

                  <div className="flex items-center gap-3">
                    <Link
                      to={`/category/${comic.category}`}
                      className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      开始阅读
                    </Link>
                    <button className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 hover:bg-background-200 text-foreground-500 transition-colors cursor-pointer" aria-label="收藏">
                      <i className="ri-heart-line text-lg"></i>
                    </button>
                    <button className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 hover:bg-background-200 text-foreground-500 transition-colors cursor-pointer" aria-label="分享">
                      <i className="ri-share-line text-lg"></i>
                    </button>
                  </div>
                </div>
              </div>

              <section className="mb-10">
                <h2 className="font-heading text-xl text-foreground-900 mb-4">
                  章节目录
                </h2>
                <div className="border border-background-200 rounded-lg divide-y divide-background-200">
                  {comic.chapters.map((chapter) => (
                    <div
                      key={chapter.id}
                      className="flex items-center justify-between px-4 py-3 hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <span className="text-sm text-foreground-700">{chapter.title}</span>
                      <span className="text-xs text-foreground-500">{chapter.updatedAt}</span>
                    </div>
                  ))}
                </div>
              </section>

              {comic.status === '连载中' && (
                <section className="mb-10 p-6 rounded-lg bg-background-100 text-center">
                  <p className="text-sm text-foreground-600 mb-3">
                    本作品正在连载中，下拉式阅读持续更新，敬请期待下一话！
                  </p>
                  <p className="text-xs text-foreground-500">
                    最近更新：{comic.updatedAt}
                  </p>
                </section>
              )}

              {relatedComics.length > 0 && (
                <section>
                  <h2 className="font-heading text-xl text-foreground-900 mb-5">
                    相关推荐
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {relatedComics.map((rc) => (
                      <Link
                        key={rc.id}
                        to={`/article/${rc.slug}`}
                        className="group cursor-pointer"
                      >
                        <div className="w-full aspect-[3/4] rounded-lg overflow-hidden mb-2.5">
                          <img
                            src={rc.cover}
                            alt={rc.title}
                            title={rc.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        </div>
                        <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                          {rc.title}
                        </h3>
                        <p className="text-xs text-foreground-500 mt-0.5">{rc.author}</p>
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </article>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}